import type { State } from './store';
import type { BandRole, Profile } from '../core/types';
import { NM } from '../core/constants';
import { classMean } from '../render/sam';


/** Short keys, because this ends up in a link somebody pastes into Slack. */
export function encodeState(p: Profile, s: State): string {
  const o: Record<string, unknown> = {
    u: p.url, r: p.bands.map((b) => b.role), d: p.domain.selected,
    m: s.mode, i: s.indexId, p: s.rampId,
    lo: +s.lo.toFixed(6), hi: +s.hi.toFixed(6), g: +s.gamma.toFixed(2),
    sb: s.singleBand, cb: s.customBands, mm: s.maskMode, su: s.scaleUnits,
    bm: s.basemap, op: +s.opacity.toFixed(2),
    f: (s.clipRange ? 1 : 0) | (s.balanceBands ? 2 : 0) | (s.lockRamp ? 4 : 0)
      | (s.overTrue ? 8 : 0) | (s.classify ? 16 : 0) | (s.theme === 'light' ? 32 : 0),
  };
  if (s.lockRamp && s.rampLo != null) { o['rl'] = +s.rampLo.toFixed(6); o['rh'] = +s.rampHi!.toFixed(6); }
  if (s.indexId === 'custom') o['x'] = s.customSrc;
  if (s.classify) { o['cn'] = s.classN; o['cm'] = s.classMethod; }
  // A classification cannot be reproduced without its training data. A brushed class
  // holds hundreds of points, which would not fit in a URL — and the classifier only
  // ever uses the mean, so a large class is encoded as its mean alone. The link
  // reproduces the map exactly; only the spread and accuracy figures are lost, both
  // being diagnostics of the training rather than part of the result.
  if (s.indexId === 'sam' && s.classes.length) {
    o['cls'] = s.classes.map((c) => {
      const mean = c.points.length > 8 ? classMean(c.points) : null;
      return {
        n: c.name, c: c.colour, k: mean ? c.points.length : undefined,
        p: (mean ? [mean] : c.points)
          .map((pt) => pt.map((x) => [x.bandIndex, +x.val.toFixed(6), x.nm, x.role])),
      };
    });
    o['ma'] = s.samMaxAngle;
  }
  return encodeURIComponent(JSON.stringify(o));
}

export interface DecodedState { url: string; raw: Record<string, unknown> }

export function decodeState(hash: string): DecodedState | null {
  try {
    const raw = decodeURIComponent(String(hash ?? '').replace(/^#/, ''));
    if (!raw) return null;
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (!o || typeof o !== 'object' || typeof o['u'] !== 'string') return null;
    return { url: o['u'], raw: o };
  } catch { return null; }
}

/** Applied after probing, so a shared link overrides whatever was inferred. */
export function applyDecoded(
  o: Record<string, unknown>, p: Profile,
): { state: Partial<State>; roles: (BandRole | null)[] } {
  const st: Partial<State> = {};
  const num = (k: string) => (typeof o[k] === 'number' ? (o[k] as number) : undefined);
  const str = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : undefined);
  if (str('m')) st.mode = o['m'] as State['mode'];
  if (str('i')) st.indexId = o['i'] as string;
  if (str('p')) st.rampId = o['p'] as string;
  if (num('lo') !== undefined) st.lo = num('lo')!;
  if (num('hi') !== undefined) st.hi = num('hi')!;
  if (num('g') !== undefined) st.gamma = num('g')!;
  if (num('sb') !== undefined) st.singleBand = num('sb')!;
  if (Array.isArray(o['cb'])) st.customBands = o['cb'] as [number, number, number];
  if (str('mm')) st.maskMode = o['mm'] as State['maskMode'];
  if (str('su')) st.scaleUnits = o['su'] as State['scaleUnits'];
  if (str('bm')) st.basemap = o['bm'] as State['basemap'];
  if (num('op') !== undefined) st.opacity = num('op')!;

  if (str('x')) st.customSrc = o['x'] as string;
  const f = (o['f'] as number) | 0;
  st.clipRange = !!(f & 1); st.balanceBands = !!(f & 2); st.lockRamp = !!(f & 4);
  st.overTrue = !!(f & 8); st.classify = !!(f & 16);
  st.theme = (f & 32) ? 'light' : 'dark';
  if (st.lockRamp && num('rl') !== undefined) { st.rampLo = num('rl')!; st.rampHi = num('rh')!; }
  if (st.classify) {
    if (num('cn') !== undefined) st.classN = num('cn')!;
    if (str('cm')) st.classMethod = o['cm'] as 'equal' | 'quantile';
  }
  if (typeof o['d'] === 'number' && p.domain.candidates[o['d'] as number]) {
    p.domain.selected = o['d'] as number;
  }
  const roles = Array.isArray(o['r']) ? (o['r'] as BandRole[]) : [];
  const cls = o['cls'] as
    { n: string; c: [number, number, number]; p: [number, number, number, string][][] }[] | undefined;
  if (Array.isArray(cls)) {
    st.classes = cls.map((c, i) => ({
      id: `c${i}`, name: c.n, colour: c.c,
      points: c.p.map((pt) => pt.map(([bandIndex, val, nm, role]) =>
        ({ bandIndex, val, nm, role }))),
    }));
    if (typeof o['ma'] === 'number') st.samMaxAngle = o['ma'];
  }
  return { state: st, roles };
}

export const roleWavelength = (r: BandRole): number | null => NM[r] ?? null;
